import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { emailService } from '../services/emailService';

const EmailVerification = () => {
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'expired'
    const [message, setMessage] = useState('Verifying your email address...');
    const [resendEmail, setResendEmail] = useState('');
    const [isResending, setIsResending] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const verifyEmailToken = async () => {
            // Get token from URL parameters
            const urlParams = new URLSearchParams(location.search);
            const token = urlParams.get('token');

            if (!token) {
                setStatus('error');
                setMessage('No verification token provided. Please check your email link.');
                return;
            }

            try {
                const result = await emailService.verifyEmail(token);

                if (result.verified || result.alreadyVerified) {
                    setStatus('success');
                    setMessage(result.message || 'Email verified successfully! Welcome to the Infinity waitlist.');

                    // Redirect to home page after 4 seconds
                    setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 4000);
                } else {
                    setStatus('error');
                    setMessage(result.message || 'Verification failed');
                }
            } catch (error) {
                console.error('Verification error:', error);

                if (error.expired) {
                    setStatus('expired');
                    setMessage(error.error || 'Verification link has expired. Please request a new one below.');
                } else {
                    setStatus('error');
                    setMessage(error.error || 'Verification failed. Please try again.');
                }
            }
        };

        verifyEmailToken();
    }, [location.search, navigate]);

    const handleResendVerification = async (e) => {
        e.preventDefault();

        if (!resendEmail.trim()) {
            alert('Please enter your email address');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(resendEmail)) {
            alert('Please enter a valid email address');
            return;
        }

        setIsResending(true);

        try {
            const result = await emailService.resendVerification(resendEmail);
            alert('Verification email sent! Please check your inbox and spam folder.');
            setResendEmail('');
        } catch (error) {
            alert(error.error || 'Failed to resend verification email. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'verifying':
                return '⏳';
            case 'success':
                return '✅';
            case 'expired':
                return '⏰';
            case 'error':
            default:
                return '❌';
        }
    };

    const getStatusColor = () => {
        switch (status) {
            case 'success':
                return 'text-green-400';
            case 'expired':
                return 'text-yellow-400';
            case 'error':
                return 'text-red-400';
            case 'verifying':
            default:
                return 'text-blue-400';
        }
    };

    const getBackgroundColor = () => {
        switch (status) {
            case 'success':
                return 'bg-green-400/10 border-green-400/20';
            case 'expired':
                return 'bg-yellow-400/10 border-yellow-400/20';
            case 'error':
                return 'bg-red-400/10 border-red-400/20';
            case 'verifying':
            default:
                return 'bg-blue-400/10 border-blue-400/20';
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4"
            style={{
                backgroundImage: 'url("/Home8.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/70"></div>

            <div className="relative z-10 max-w-md w-full bg-[#1a1a1a]/90 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center">
                    {/* Status Icon */}
                    <div className="text-6xl mb-4">
                        {getStatusIcon()}
                    </div>

                    {/* Title */}
                    <h1 className="text-2xl font-bold text-white mb-4">
                        {status === 'verifying' && 'Verifying Email...'}
                        {status === 'success' && 'Email Verified!'}
                        {status === 'expired' && 'Link Expired'}
                        {status === 'error' && 'Verification Failed'}
                    </h1>

                    {/* Message */}
                    <div className={`rounded-lg border p-4 mb-6 ${getBackgroundColor()}`}>
                        <p className={`text-lg ${getStatusColor()}`}>
                            {message}
                        </p>
                    </div>

                    {/* Success Actions */}
                    {status === 'success' && (
                        <div className="space-y-4">
                            <div className="bg-green-400/10 border border-green-400/20 rounded-lg p-4">
                                <p className="text-green-400 text-sm">
                                    🎉 Welcome to the Infinity waitlist! You'll receive updates about our launch.
                                </p>
                                <p className="text-white/60 text-xs mt-2">
                                    Redirecting to homepage in a few seconds...
                                </p>
                            </div>

                            <button
                                onClick={() => navigate('/')}
                                className="w-full bg-yellow-400 text-black font-bold py-3 px-6 rounded-lg hover:bg-yellow-300 transition"
                            >
                                Return to Homepage
                            </button>
                        </div>
                    )}

                    {/* Expired/Error Actions */}
                    {(status === 'expired' || status === 'error') && (
                        <div className="space-y-4">
                            <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-4">
                                <p className="text-yellow-400 text-sm mb-3">
                                    Need a new verification link? Enter your email below:
                                </p>

                                <form onSubmit={handleResendVerification} className="space-y-3">
                                    <input
                                        type="email"
                                        placeholder="your.email@example.com"
                                        value={resendEmail}
                                        onChange={(e) => setResendEmail(e.target.value)}
                                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                        required
                                    />

                                    <button
                                        type="submit"
                                        disabled={isResending}
                                        className="w-full bg-yellow-400 text-black font-bold py-2 px-4 rounded-lg hover:bg-yellow-300 transition disabled:opacity-50"
                                    >
                                        {isResending ? 'Sending...' : 'Resend Verification'}
                                    </button>
                                </form>
                            </div>

                            <button
                                onClick={() => navigate('/')}
                                className="w-full border border-white/20 text-white py-3 px-6 rounded-lg hover:bg-white/5 transition"
                            >
                                Return to Homepage
                            </button>
                        </div>
                    )}

                    {/* Loading State */}
                    {status === 'verifying' && (
                        <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-white/20 text-center">
                    <p className="text-white/60 text-sm">
                        Questions? Contact us at{' '}
                        <a href="mailto:info@theplatypus.in" className="text-yellow-400 hover:underline">
                            info@theplatypus.in
                        </a>
                    </p>

                    {/* Platypus branding */}
                    <div className="mt-4 flex items-center justify-center space-x-2">
                        <img src="/petpawicon.svg" alt="Platypus" className="w-4 h-4" />
                        <span className="text-white/40 text-xs">Platypus - Be Their Human</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;